import React, { useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import classNames from 'classnames';
import { useForm } from 'react-hook-form';
import { isMobile } from 'react-device-detect';
import * as ReactDOM from 'react-dom';
import LeafletMap from '../../../components/base/Map/LeafletMap';
import { carIcon } from '../../../components/base/Map/icons/icons';
import { Button } from '../../../components/base/BootstrapComponents/BootstrapComponents';
import {
  createClearObject, getFilterData, responseLogWithNotification, useUpdatePeriod,
} from '../../../utils/utils';
import { getVehicleRegistryData } from '../reducer';
import { FullScreenControl } from '../../../components/base/Map/FullScreenControl';
import RectangleDrawer from '../../../components/base/Map/RectangleDrawer';
import HoverMarker from '../../../components/base/Map/HoverMarker';
import {
  centrifugeConnection, centrifugeDisconnection,
  centrifugeSubscribe,
  centrifugeUnsubscribe,
  trackingSubscribe,
  sendTrackingRefresh,
} from '../../Centrifuge/actions';
import { MAP_EVENT } from '../actions';
import { VEHICLES_REGISTRY_MODULE } from '../../../configs/moduleNames';
import { PAGE_TYPE } from '../../FilterTemplate/components/filter-settings';
import FilterForm from '../../FilterTemplate/components/FilterForm';
import VehicleFilterRow from './VehicleFilterRow';
import MobileVehicleFooter from './MobileVehicleFooter';

export default function VehiclesMap(props) {
  const { handleOpenDetails } = props;
  const { formatMessage } = useIntl();
  const { vehicleRegistryOnMapList = [] } = useSelector(getVehicleRegistryData);
  const filterName = 'vehicle-map-registry-filter';
  const defaultFilterValues = {};
  const chanel = useRef(null);
  const map = useRef(null);
  const [fullScreen, setFullScreen] = useState(false);
  const [bound, setBound] = useState(null);
  const [isOpenFilter, setOpenFilter] = useState(!isMobile);
  const dispatch = useDispatch();
  const keepAlivePeriod = 5 * 60;
  const {
    getValues, watch, control, reset, setValue,
  } = useForm({ defaultValues: getFilterData(filterName, defaultFilterValues) });
  const getBound = () => {
    const last_coordinate = getValues('last_coordinate');
    if (
      map?.current && (!Array.isArray(last_coordinate) || !last_coordinate?.length)
    ) {
      const bound = map?.current.getBounds();
      const southWest = bound.getSouthWest();
      const northEast = bound.getNorthEast();
      return {
        point1: { lat: northEast.lat, lon: northEast.lng },
        point2: { lat: southWest.lat, lon: southWest.lng },
      };
    }
    return {
      point1: { lat: last_coordinate[0][0], lon: last_coordinate[0][1] },
      point2: { lat: last_coordinate[1][0], lon: last_coordinate[1][1] },
    };
  };

  const loadData = () => dispatch(sendTrackingRefresh({ filter: getValues(), bounds: getBound(), channel_id: chanel.current })).catch(responseLogWithNotification);
  const [setPeriod, destroy] = useUpdatePeriod(loadData, keepAlivePeriod);

  useEffect(() => {
    initBound(getValues());
    return () => {
      if (map?.current) {
        dispatch(centrifugeUnsubscribe()).then(() => {
          dispatch(centrifugeDisconnection()).catch(responseLogWithNotification);
        }).catch(responseLogWithNotification);
        destroy();
      }
    };
  }, []);
  useEffect(() => {
    const subscription = watch(values => {
      localStorage.setItem(filterName, JSON.stringify(values));
      loadData(values);
      initBound(values);
    });
    return () => subscription.unsubscribe();
  }, [watch]);

  const initBound = values => {
    const last_coordinate = values?.last_coordinate || [];
    if (!Array.isArray(last_coordinate) || !last_coordinate?.length) {
      setBound(null);
    } else {
      setBound(last_coordinate);
    }
  };

  const setMap = m => {
    map.current = m;
    dispatch(centrifugeConnection()).then(() => {
      dispatch(trackingSubscribe({ filters: getValues(), bounds: getBound() })).then(data => {
        dispatch(centrifugeUnsubscribe(data)).then(() => {
          dispatch(centrifugeSubscribe({ ...data, type: MAP_EVENT, module: VEHICLES_REGISTRY_MODULE })).then(() => {
            chanel.current = data?.channel_id;
            setPeriod(keepAlivePeriod);
          }).catch(responseLogWithNotification);
        }).catch(responseLogWithNotification);
      }).catch(responseLogWithNotification);
    }).catch(responseLogWithNotification);
  };

  const clear = () => {
    const fields = getValues();
    const cleared = createClearObject(fields);
    reset({ ...cleared, ...defaultFilterValues });
  };

  const handleChangeRectangle = polygon => {
    if (
      !polygon
      || !polygon[0][0]
      || !polygon[0][1]
      || !polygon[1][0]
      || !polygon[0][1]
    ) {
      return;
    }
    setValue('last_coordinate', polygon);
  };

  const renderFooter = () => {
    const element = document.getElementById('app-content-footer');
    if (!element) {
      return null;
    }
    return ReactDOM.createPortal(<MobileVehicleFooter
      control={control}
      defaultFilterValues={defaultFilterValues}
      getValues={getValues}
      reset={reset}
      page={PAGE_TYPE.VEHICLE_REGISTRY}
      hidePeriod
    />, element);
  };

  return (
    <>
      {!isMobile ? (
        <VehicleFilterRow
          selectedTab={2}
          toggleFilter={() => setOpenFilter(!isOpenFilter)}
          control={control}
        />
      ) : null}
      <div className={classNames('map-columns card', { 'map-fullscreen': fullScreen })}>
        {/* { 'find-mode': isBound } */}
        <div className="map-box">
          <div className="map-wrapper">
            <LeafletMap classNames="stops-map" setMap={setMap} onMove={loadData} onMoveEnd={loadData}>
              {vehicleRegistryOnMapList.map((vehicle, key) => {
                const { location, registryNumber } = vehicle;
                const lat = Number(location.latitude);
                const lon = Number(location.longitude);
                if (Number.isNaN(lat) || Number.isNaN(lon)) return null;
                const coords = [lat, lon];
                return (
                  <HoverMarker
                    key={key}
                    position={coords}
                    icon={carIcon}
                    onClick={() => handleOpenDetails(vehicle)}
                    text={vehicle.registryNumber}
                  />
                );
              })}
              <FullScreenControl onClick={() => setFullScreen(!fullScreen)} fullScreen={fullScreen} />
              <RectangleDrawer onChange={handleChangeRectangle} getValues={getValues} bound={bound} watch={watch} />
            </LeafletMap>
          </div>
        </div>
        {/* {isOpenMapFilter && !isBound ? ( */}
        {isOpenFilter ? (
          <div className="map-filter">
            <div className="filter-header f3">Фильтр</div>
            <div className="filter-content custom-scroll-theme">
              <div className="filter-content-block custom-scroll-theme">
                <div className="filter-content-wrapper">
                  <FilterForm page={PAGE_TYPE.VEHICLE_REGISTRY} control={control} />
                </div>
              </div>
            </div>
            <div className="filter-footer">
              <div
                className="hff-footer-text"
              >{formatMessage({ id: 'find' })} {vehicleRegistryOnMapList?.length || 0} автомобилей
              </div>
              <div className="hff-footer-buttons hff-center">
                <Button
                  className="hff-clear-data"
                  variant="secondary"
                  onClick={clear}
                >{formatMessage({ id: 'clear.all' })}
                </Button>
                <Button className="hff-clear-show" onClick={() => setOpenFilter(!isOpenFilter)}>{formatMessage({ id: 'show' })}</Button>
              </div>
            </div>
          </div>
        ) : null}
      </div>
      {isMobile ? renderFooter() : null}
    </>

  );
}
